import { useCallback } from 'react'
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form'

import {
	convertTagsToBackendFormat,
	convertTagsToFormFormat
} from '@/features/FormUploader/utils/tagsTransformers'
import { updateFormByField } from '@/features/Home/components/shotlistUpload/helper'
import {
	IFetchUploadForm,
	IUploaderForm,
	TPlatformValue
} from '@/types/uploadForm.interface'

interface UseFieldChangeProps {
	taskId: string
	selectedPlatform: TPlatformValue
	uploaderFormGetValues: UseFormGetValues<IUploaderForm>
	uploaderFormSetValue: UseFormSetValue<IUploaderForm>
	updatePlatformCompletion: (
		platform: TPlatformValue,
		formData: IUploaderForm
	) => void
}

export const useFieldChange = ({
	taskId,
	selectedPlatform,
	uploaderFormGetValues,
	uploaderFormSetValue,
	updatePlatformCompletion
}: UseFieldChangeProps) => {
	return useCallback(
		async (
			fieldName: keyof IUploaderForm,
			value: IUploaderForm[keyof IUploaderForm]
		) => {
			try {
				// Convert tags from IOption[] to {name, id}[] format if field is tags
				let valueForBackend:
					| IUploaderForm[keyof IUploaderForm]
					| Array<{ name: string; id: string }> = value
				if (fieldName === 'tags' && Array.isArray(value)) {
					valueForBackend = convertTagsToBackendFormat(value)
				}

				const response = await updateFormByField({
					task_id: taskId,
					platform: selectedPlatform,
					field: fieldName,
					value: valueForBackend as IUploaderForm[keyof IUploaderForm],
					is_completed: false
				})

				if (!response) {
					const current = uploaderFormGetValues()
					const next = { ...current, [fieldName]: value } as IUploaderForm
					updatePlatformCompletion(selectedPlatform, next)
					return
				}

				const responseData = response as IFetchUploadForm
				if (
					responseData.platforms?.[selectedPlatform]?.[fieldName] !== undefined
				) {
					let serverValue =
						responseData.platforms?.[selectedPlatform]?.[fieldName]

					// Convert tags from {name, id}[] to IOption[] format if field is tags
					if (fieldName === 'tags' && Array.isArray(serverValue)) {
						const tagsFromServer = serverValue as Array<{
							name?: string
							id?: string | number
						}>
						serverValue = convertTagsToFormFormat(
							tagsFromServer
						) as unknown as IUploaderForm['tags']
					}

					uploaderFormSetValue(fieldName, serverValue)

					setTimeout(() => {
						const current = uploaderFormGetValues()
						const next = {
							...current,
							[fieldName]: serverValue
						} as IUploaderForm
						updatePlatformCompletion(selectedPlatform, next)
					}, 0)
				} else {
					setTimeout(() => {
						const currentFormData = uploaderFormGetValues()
						updatePlatformCompletion(selectedPlatform, currentFormData)
					}, 0)
				}
			} catch (error) {
				console.error(`Error updating field ${fieldName}:`, error)
			}
		},
		[
			taskId,
			selectedPlatform,
			uploaderFormGetValues,
			uploaderFormSetValue,
			updatePlatformCompletion
		]
	)
}
